<template>
  <div class="user-form" v-if="show" data-testid="UserForm">
    <page-title-area>
      <entity-header icon="user">
        {{ heading }}
        <i v-if="isDistributorDisplayed">({{ $t('label.distributor') }})</i>
      </entity-header>
    </page-title-area>
    <div class="user-form__content">
      <div class="user-form__form my-3">
        <v-form v-model="valid" ref="form">
          <form-group v-if="!(isDistributorCreation || isAdministrativeUserDisplayed)">
            <template #label><span>{{ $t('field.organization') }}</span></template>
            <template #input>
              <select-field
                :items="organization_options"
                v-model="model.owner_id"
                :edit="state.editing"
                data-testid="owner_id"
                v-if="admin"
              />
              <span v-else>
                {{ organization.name }}
              </span>
            </template>
          </form-group>
          <form-group>
            <template #label><span>{{ nameLabel }}</span></template>
            <template #input>
              <text-field
                v-model="model.name"
                :edit="state.editing"
                :rules="[required(nameLabel)]"
                data-testid="name"
              />
            </template>
          </form-group>
          <form-group>
            <template #label><span>{{ $t('field.email') }}</span></template>
            <template #input>
              <text-field
                type="email"
                v-model="model.email"
                :edit="state.editing"
                :rules="[required($t('field.email')), email($t('field.email'))]"
                data-testid="email"
              />
            </template>
          </form-group>
          <form-group>
            <template #label><span>{{ $t('field.locale') }}</span></template>
            <template #input>
              <select-field
                :items="locale_options"
                v-model="model.locale"
                :edit="state.editing"
                :rules="[required($t('field.locale'))]"
                data-testid="locale"
              />
            </template>
          </form-group>
          <form-group v-if="mirakunDeviceEnabled">
            <template #label><span>{{ $t('field.mirakun_user_id') }}</span></template>
            <template #input>
              <text-field
                v-model="model.mirakun_user_id"
                :edit="state.editing"
                data-testid="mirakun_user_id"
              />
            </template>
          </form-group>
          <form-group>
            <template #label><span>{{ $t('field.mfa_settings') }}</span></template>
            <template #input>
              <checkbox
                v-model="model.use_otp"
                :label="$t('field.otp_authentication')"
                :edit="state.editing"
                data-testid="use_otp"
              />
            </template>
          </form-group>
          <form-group v-if="state.editing">
            <template #label><span>{{ $t('field.password') }}</span></template>
            <template #input>
              <text-field
                type="password"
                v-model="model.password"
                :edit="state.editing"
                :rules="[rules.password_required, password_length(), password_characters()]"
                data-testid="password"
              />
            </template>
          </form-group>
          <form-group v-if="state.editing">
            <template #label><span>{{ $t('field.password_confirm') }}</span></template>
            <template #input>
              <text-field
                type="password"
                v-model="model.password_confirm"
                :edit="state.editing"
                :rules="[match_password()]"
                data-testid="password_confirm"
              />
            </template>
          </form-group>
          <form-group v-if="admin">
            <template #label><span>{{ $t('field.invited_by') }}</span></template>
            <template #input>
              {{ user.invited_by | invitedBy }}
            </template>
          </form-group>
        </v-form>
      </div>
      <div class="user-form__btn-group">
        <template v-if="state.editing">
          <round-button
            class="user-form__btn my-3"
            icon="fa-check-circle"
            fullWidth
            title="save"
            @click="onSave"
            data-testid="save"
          >
            {{ $t('button.save') }}
          </round-button>
          <round-button
            class="user-form__btn my-3"
            icon="fa-arrow-circle-left"
            fullWidth
            @click="onCancel"
            data-testid="cancel"
          >
            {{ $t('button.cancel') }}
          </round-button>
        </template>
        <template v-else>
          <round-button
            class="user-form__btn my-3"
            icon="fa-pencil-square-o"
            fullWidth
            @click="onEdit"
            data-testid="edit"
          >
            {{ $t('button.edit') }}
          </round-button>
          <template v-if="admin">
            <round-button
              class="user-form__btn my-3"
              :icon="model.activated ? 'fa-minus-circle' : 'fa-plus-circle'"
              fullWidth
              @click="onToggleActivated"
              data-testid="toggle-activated"
            >
              {{ model.activated ? $t('button.deactivate') : $t('button.activate') }}
            </round-button>
          </template>
          <template v-if="admin && model.blocked">
            <round-button
              class="user-form__btn my-3"
              icon="fa-plus-circle"
              fullWidth
              @click="onUnblock"
              data-testid="unblock"
            >
              {{ $t('button.unblock') }}
            </round-button>
          </template>
          <template v-if="canLoginAsUser">
            <round-button
              class="user-form__btn my-3"
              fullWidth
              @click="onLoginAsUser"
              data-testid="login-as-user"
            >
              {{ $t('button.login_as_user') }}
            </round-button>
          </template>
        </template>
      </div>
    </div>
    <confirm-dialog
      v-model="invitationConfirm.visible"
      :message="invitationConfirm.message"
      @yes="invitationConfirm.onConfirm"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  field:
    organization: '組織'
    name: 'ユーザー名'
    distributor_name: '代理店名'
    email: 'E-mail'
    password: '新しいパスワード'
    password_confirm: '新しいパスワード（確認）'
    locale: '言語'
    mirakun_user_id: 'mirakun.comのID'
    invited_by: '共有元'
    mfa_settings: '多要素認証設定'
    otp_authentication: 'ワンタイムパスワードを有効にする'

  label:
    create_account: 'アカウント作成'
    create_distributor_account: '代理店アカウント作成'
    distributor: '代理店'

  msg:
    deactivate_confirm: 'このアカウントの利用を停止します、よろしいですか？'
    update_user_success: 'アカウント情報を更新しました。'
    update_user_error: 'アカウント情報を更新しようとして、エラーが発生しました。'
    create_user_success: 'アカウントを作成しました。'
    create_user_error: 'アカウントを作成しようとして、エラーが発生しました。'
    email_already_exists: '指定されたメールアドレスはすでに登録されています。'
    login_as_user_error: '代理ログインできませんでした。'
    invitation_pending_with_organization: >-
      このメールアドレスは {organization} の
      {name} さんから招待されています。招待を取り消して、入力した内容でアカウントを作成しますか？
    invitation_pending: 'このメールアドレスは {name} さんから招待されています。招待を取り消して、入力した内容でアカウントを作成しますか？'
    invitation_pending_elsewhere: 'このメールアドレスは他の管轄で招待中のため作成できません。管理者にお問い合わせください。'
    organization_outside_jurisdiction: '指定された組織には所属させられません。'

  button:
    save: '保存'
    cancel: 'キャンセル'
    activate: 'アカウント有効化'
    deactivate: 'アカウント無効化'
    unblock: 'ブロックを解除'
    edit: '編集'
    login_as_user: '代理ログイン'

en:
  field:
    organization: 'Organization'
    name: 'Name'
    distributor_name: 'Distributor Name'
    email: 'E-mail'
    password: 'New password'
    password_confirm: 'New password (confirm)'
    locale: 'Language'
    mirakun_user_id: 'mirakun.com User ID'
    invited_by: 'Invited by'
    mfa_settings: 'MFA Settings'
    otp_authentication: 'Enable OTP Authentication'

  label:
    create_account: 'Create Account'
    create_distributor_account: 'Create Distributor Account'
    distributor: 'distributor'

  msg:
    deactivate_confirm: 'This account will be deactivated. Are you sure?'
    update_user_success: 'Account information was successfully updated.'
    update_user_error: 'An error occurred while updating the account information.'
    create_user_success: 'Account was successfully created.'
    create_user_error: 'An error occurred while creating the account.'
    email_already_exists: 'This email address is already registered.'
    login_as_user_error: 'Failed to log in as this user.'
    invitation_pending_with_organization: >-
      This email address has been invited by {name} of {organization}.
      Do you want to cancel the invitation and create the account with the entered information?
    invitation_pending: >-
      This email address has been invited by {name}.
      Do you want to cancel the invitation and create the account with the entered information?
    invitation_pending_elsewhere: >-
      This email address is invited in another jurisdiction and cannot be used. Please contact the administrator.
    organization_outside_jurisdiction: 'The account cannot belong to the specified organization.'

  button:
    save: 'Save'
    cancel: 'Cancel'
    activate: 'Activate account'
    deactivate: 'Deactivate account'
    unblock: 'Unblock account'
    edit: 'Edit'
    login_as_user: 'Login as User'

</i18n>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Features from '@/mixins/features';
import Locales from '@/mixins/locales';
import Notifications from '@/mixins/notifications';
import SensorTypes from '@/mixins/sensorTypes';

import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import EntityHeader from '@/components/atoms/EntityHeader';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import RoundButton from '@/components/atoms/RoundButton';
import FormGroup from './Form/FormGroup';
import TextField from './Form/TextField';
import SelectField from './Form/SelectField';
import Validations from './Form/mixins/form-validations';
import Checkbox from './Form/Checkbox';

const DEFAULT_MODEL = {
  owner_id: null,
  name: '',
  email: '',
  locale: '',
  mirakun_user_id: '',
  password: '',
  password_confirm: '',
  activated: false,
  blocked: false,
  use_otp: false,
};

const buildModel = user => ({
  ...DEFAULT_MODEL,
  ..._.pick(user, Object.keys(DEFAULT_MODEL)),
});

export default {
  name: 'user-form',
  mixins: [
    Features,
    Locales,
    Notifications,
    Validations,
    SensorTypes,
  ],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    newRecord: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Checkbox,
    ConfirmDialog,
    EntityHeader,
    FormGroup,
    PageTitleArea,
    RoundButton,
    SelectField,
    TextField,
  },
  inject: ['assumeUserIdentity'],
  async beforeMount() {
    if (this.newRecord) {
      this.show = true;
    }
    if (this.admin) {
      if (!this.newRecord) {
        await this.requestUser(this.$route.params.email);
      }
      await this.requestOrganizations();
    } else {
      await this.getUserInfo();
      const user = this.getCurrentUser;
      // 代理店など組織に所属しないユーザーは組織を取得しない
      if (user.owner_id) {
        await this.requestOrganization(user.owner_id);
      }
    }
  },
  data() {
    return {
      show: false,
      valid: false,
      model: buildModel({}),
      state: {
        editing: this.newRecord,
      },
      rules: {
        password_required: this.newRecord ? this.required(this.$t('field.password')) : true,
      },
      invitationConfirm: {
        visible: false,
        message: '',
        onConfirm: () => {},
      },
    };
  },
  computed: {
    ...mapGetters([
      'getUser',
      'getCurrentUser',
      'getOrganization',
      'assumedUser',
    ]),
    // 代理店も /admin/accounts/new に入れるため、admin prop ではなくユーザー自身の権限で判定する。
    // 条件を満たさずに ?type=distributor を直打ちした場合は通常のアカウント作成として扱う
    isDistributorCreation() {
      return this.newRecord &&
        this.getCurrentUser.admin &&
        this.featureIsActive('distributor_account') &&
        this.$route.query.type === 'distributor';
    },
    isDistributorDisplayed() {
      return this.admin && !!this.user.distributor;
    },
    // admin・代理店アカウントは組織に所属しないため、管理画面の詳細・本人のアカウント画面とも組織欄を出さない
    isAdministrativeUserDisplayed() {
      const user = this.user || {};
      return !this.newRecord && !!(user.admin || user.distributor);
    },
    // 代理店アカウントの作成・明細（管理画面・本人のアカウント画面とも）では名前欄を代理店名として扱う
    isDistributorAccountForm() {
      const user = this.user || {};
      return this.isDistributorCreation || (!this.newRecord && !!user.distributor);
    },
    nameLabel() {
      return this.isDistributorAccountForm ? this.$t('field.distributor_name') : this.$t('field.name');
    },
    heading() {
      if (!this.newRecord) {
        return this.user.name;
      }
      return this.isDistributorCreation ? this.$t('label.create_distributor_account') : this.$t('label.create_account');
    },
    isAdminUserDisplayed() {
      return this.user.admin;
    },
    canLoginAsUser() {
      // 管理者と代理店が使える。代理ログイン中は多段の代理ログインを防ぐため出さない
      const currentUser = this.getCurrentUser;
      return this.admin && !!(currentUser.admin || currentUser.distributor) && !this.assumedUser && !this.model.blocked
        && this.model.activated && !this.isAdminUserDisplayed;
    },
    user() {
      if (this.newRecord) {
        return {};
      }
      if (this.admin) {
        return this.getUser(this.$route.params.email);
      }
      return this.getCurrentUser;
    },
    mirakunDeviceEnabled() {
      return this.user?.allowed_sensor_types?.includes(this.SENSOR_TYPES.MIRAKUN_DEVICE) || false;
    },
    organization() {
      try {
        const user = this.getCurrentUser;
        return this.getOrganization(user.owner_id) || {};
      } catch (error) {
        // テスト環境でgetOrganizationが利用できない場合は空オブジェクトを返す
        return {};
      }
    },
    organization_options() {
      const organizations = this.$store.getters.getOrganizations;
      return [
        { text: '', value: '' },
        ...organizations.map((org) => ({
          text: org.name,
          value: org.id,
        })),
      ];
    },
  },
  methods: {
    ...mapActions([
      'createUser',
      'getUserInfo',
      'updateUser',
      'updateUserInfo',
      'requestUser',
      'requestOrganization',
      'requestOrganizations',
    ]),
    onLoginAsUser: async function () {
      try {
        await this.assumeUserIdentity(this.user);
      } catch (error) {
        this.notifyError('msg.login_as_user_error');
        return;
      }
      this.$router.push('/');
    },
    createAccount: async function (params) {
      try {
        await this.createUser(params);
      } catch (error) {
        if (this.errorType(error) === 'invitation_pending') {
          this.confirmInvitationDiscard(params, error.response.data.invited_by);
          return;
        }
        if (this.errorType(error) === 'invitation_pending_elsewhere') {
          this.notifyError('msg.invitation_pending_elsewhere');
          return;
        }
        if (this.errorType(error) === 'organization_outside_jurisdiction') {
          this.notifyError('msg.organization_outside_jurisdiction');
          return;
        }
        if (this.isAlreadyExistsError(error)) {
          this.notifyError('msg.email_already_exists');
          return;
        }
        this.notifyError('msg.create_user_error');
        return;
      }
      this.notifySuccess('msg.create_user_success');
      this.$router.push(`/admin/accounts/${params.email}`);
      // Because component doesn't change it will not recreate data
      // So we need to reset this manually
      this.rules.password_required = true;
      this.state.editing = false;
    },
    confirmInvitationDiscard(params, invitedBy) {
      const name = this._.escape(invitedBy.name);
      this.invitationConfirm.message = invitedBy.organization_name
        ? this.$t('msg.invitation_pending_with_organization', {
          organization: this._.escape(invitedBy.organization_name), name,
        })
        : this.$t('msg.invitation_pending', { name });
      this.invitationConfirm.onConfirm = () => this.createAccount({ ...params, discard_invitation: true });
      this.invitationConfirm.visible = true;
    },
    handleUpdateError: async function (operation) {
      try {
        await operation();
      } catch (error) {
        if (this.isAlreadyExistsError(error)) {
          this.notifyError('msg.email_already_exists');
          return;
        }
        if (this.errorType(error) === 'organization_outside_jurisdiction') {
          this.notifyError('msg.organization_outside_jurisdiction');
          return;
        }
        this.notifyError('msg.update_user_error');
      }
    },
    errorType(error) {
      return this._.get(error, 'response.data.type');
    },
    isAlreadyExistsError(error) {
      return this.errorType(error) === 'resource_already_exists';
    },
    onCancel() {
      if (this.newRecord) {
        this.$router.push('/admin/accounts');
      }
      this.model = buildModel(this.user);
      this.state.editing = false;
    },
    onEdit() {
      this.state.editing = true;
    },
    onSave: async function () {
      if (this.$refs.form.validate()) {
        const params = this.pickParams();
        if (this.admin) {
          if (this.newRecord) {
            this.createAccount(params);
          } else {
            this.handleUpdateError(async () => {
              await this.updateUser({ email: this.user.email, attributes: params });
              this.notifySuccess('msg.update_user_success');
              this.state.editing = false;
            });
          }
        } else {
          this.handleUpdateError(async () => {
            await this.updateUserInfo(params);
            this.notifySuccess('msg.update_user_success');
            this.state.editing = false;
          });
        }
      }
    },
    onToggleActivated: async function () {
      this.handleUpdateError(async () => {
        await this.updateUser({
          email: this.user.email,
          attributes: { activated: !this.model.activated },
        });
        this.notifySuccess('msg.update_user_success');
      });
    },
    onUnblock: async function () {
      this.handleUpdateError(async () => {
        await this.updateUser({ email: this.user.email, attributes: { blocked: false } });
        this.notifySuccess('msg.update_user_success');
      });
    },
    pickParams() {
      const allowedKeys = this._.keys(DEFAULT_MODEL);
      const result = { ...this.model };

      delete result.activated;
      delete result.blocked;

      if (!result.password) {
        delete result.password;
      }
      delete result.password_confirm;

      if (this.admin) {
        if (result.owner_id === '') {
          result.owner_id = null;
        }
      }

      // 代理店は組織に所属しない
      if (this.isDistributorCreation) {
        delete result.owner_id;
      }

      if (!this.mirakunDeviceEnabled) {
        delete result.mirakun_user_id;
      }

      const params = this._.pickBy(result, (value, key) => (
        allowedKeys.includes(key) && value !== this.user[key]
      ));
      if (this.isDistributorCreation) {
        params.distributor = true;
      }
      return params;
    },
  },
  watch: {
    user(val) {
      this.model = buildModel(val);
      this.show = !!val;
    },
  },
  filters: {
    invitedBy(value) {
      if (value) {
        const { name, organization_name: organizationName } = value;
        return `${organizationName || ''} (${name})`;
      }
      return '';
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.user-form
  &__field
    display: flex

    &__icon
      font-size: 16px

  &__label,
  &__input-group
    padding-top: 4px
    padding-bottom: 4px

  &__label
    border-right: 1px solid
    padding-right: 16px
    width: 35%

  &__input-group
    flex: 1
    padding-left: 16px

    ::v-deep .input-group
      margin-top: -6px
      margin-bottom: -6px
      padding: 0 0 16px

    ::v-deep .input-group--text-field .input-group__input
      background-color: transparent
      border: 1px solid
      padding: 0 4px

      input
        width: 100%

    &--checkbox
      color: map-get($colors, 'grey', 'base') !important

      ::v-deep .input-group--selection-controls__ripple:before
        background-color: transparent !important

  &__btn-group
    margin: 24px 0

@media #{map-get($display-breakpoints, 'md-and-up')}
  .user-form
    &__content
      display: flex

    &__form
      width: 50%

    &__label,
    &__input-group
      padding: 20px 16px

    &__input-group ::v-deep .input-group
      padding: 0

    &__label
      border-right: 0

    &__btn-group
      margin: 0 40px
      width: 240px

    &__btn ::v-deep .v-btn__content
      padding: 0 32px
</style>
